import * as React from "react";
import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Map, Feature } from "ol";
import { useMap } from "../Map/useMap";
import { CustomView } from "../Views/CustomView";
import { SideView } from "../Views/SideView";
import { fetchAllFeatures, getFieldInfos } from "../Map/rest";
import { Identify } from "../Popups/Identify";
import { Downloads } from "../Popups/Downloads";
import { Apply } from "../Popups/Apply";
import { Preview } from "../Popups/Preview";
import { LayersMenu } from "../Popups/LayersMenu";
import { useNomaContext } from "../Contexts/MapContext";
import { useFiltersContext } from "../Contexts/FiltersContext";
import { CustomViewContextProvider } from "../Contexts/CustomViewContext";
import { SideViewContextProvider } from "../Contexts/SideViewContext";
import { RegistrationContextProvider } from "../Contexts/RegistrationContext";
import { filterByExtent, filterFeatures } from "../Map/filters";
import { Registration } from "../Popups/Registration/Registration";
import { objectIdGap } from "../Map/config";
import "../css/icons/style.css";
import "../css/map.scss";
import "ol/ol.css";

export interface IZemesnomaMapProps {
    features: Feature[] | null;
    forceUpdate: number;
}

let mapRef: MutableRefObject<Map>;
let allFeaturesRef: MutableRefObject<Feature[]>;
let filteredFeaturesRef: MutableRefObject<Feature[]>;
let isFetchingRef: MutableRefObject<boolean>;

export const ZemesnomaMap: FC = () => {
    const [forceUpdate, setForceUpdate] = useState<number>(0);
    const ref = useRef<HTMLDivElement>();

    const {
        activeTab,
        setFieldInfos,
        setIdentifiedFeature,
        setLocateFeature,
        identifiedFeature,
        previewFeature,
        applyFeature,
        downloadsPopupDocs,
        layersMenuVisible,
        registerFeature,
        filterDefaultValues,
        urlParams
    } = useNomaContext();

    const { extent, filters, setResultsCount } = useFiltersContext();

    mapRef = useRef<Map>();
    allFeaturesRef = useRef<Feature[]>();
    filteredFeaturesRef = useRef<Feature[]>();
    isFetchingRef = useRef<boolean>(true);

    const initIdentify = (feature: Feature) => {
        setIdentifiedFeature(feature);
        setLocateFeature(feature);
    };

    mapRef.current = useMap(ref, {
        features: filteredFeaturesRef.current,
        forceUpdate
    });

    const getExtentFeatures = useCallback(() => {
        return filterByExtent(filteredFeaturesRef.current, extent);
    }, [filteredFeaturesRef.current, extent]);

    useEffect(() => {
        if (!allFeaturesRef.current) {
            return;
        }
        filteredFeaturesRef.current = filterFeatures(allFeaturesRef.current, filters);
        filteredFeaturesRef.current && setResultsCount(filteredFeaturesRef.current.length);
        setForceUpdate(Date.now());
    }, [filters]);

    useEffect(() => {
        fetchAllFeatures(activeTab).then((features) => {
            isFetchingRef.current = false;
            allFeaturesRef.current = features;

            filteredFeaturesRef.current = filterFeatures(allFeaturesRef.current, filters);
            filteredFeaturesRef.current && setResultsCount(filteredFeaturesRef.current.length);

            setFieldInfos(getFieldInfos());

            urlParams && identifyParameterFeature();
        });
    }, []);

    useEffect(() => {
        if (isFetchingRef.current) {
            return;
        }
        fetchAllFeatures(activeTab).then((features) => {
            allFeaturesRef.current = features;
            filteredFeaturesRef.current = filterFeatures(allFeaturesRef.current, getFilters());
            allFeaturesRef.current && setResultsCount(filteredFeaturesRef.current.length);
            setForceUpdate(Date.now());
        });
    }, [activeTab]);

    const getFilters = useCallback(() => {
        return filters || filterDefaultValues;
    }, [filters, filterDefaultValues]);

    const identifyParameterFeature = () => {
        const feature = allFeaturesRef.current.find(
            (f) => f.get("LAYERID") === urlParams.layerId && f.get("OBJECTID") === Number(urlParams.objectId)
        );
        feature && initIdentify(feature);
    };

    return (
        <>
            <div className="map-container">
                <div ref={ref} className="map" />
            </div>
            <SideViewContextProvider>
                <SideView></SideView>
            </SideViewContextProvider>
            <CustomViewContextProvider>
                <CustomView features={getExtentFeatures()} onLocateClick={initIdentify}></CustomView>
                {identifiedFeature ? <Identify></Identify> : null}
                {previewFeature ? <Preview></Preview> : null}
                {applyFeature ? <Apply></Apply> : null}
            </CustomViewContextProvider>

            {downloadsPopupDocs ? <Downloads></Downloads> : null}
            {layersMenuVisible ? <LayersMenu></LayersMenu> : null}
            {registerFeature ? (
                <RegistrationContextProvider>
                    <Registration></Registration>
                </RegistrationContextProvider>
            ) : null}
        </>
    );
};

export const getMap = (): Map => mapRef.current;

export const updateMapSize = (): void => mapRef.current && mapRef.current.updateSize();
